/**
 * Rendering a generated wave plan (spec sections 33, 61).
 *
 * Shared by the live planner output and by the stored-plan view, so a plan
 * cannot look like one thing when it is generated and another when it is read
 * back out of the recommendation log.
 *
 * Assumptions, explanations and warnings arrive from the server as free text
 * and are rendered verbatim in both locales — they are the engine's own account
 * of what it did, and paraphrasing them here would put words in its mouth.
 */

import { Link } from 'react-router-dom';
import type { BearPlanResponse } from '@kso/schemas';
import { Badge, Card, Stat, Value, WarningList } from '../../components/ui/primitives';
import {
  ConfidenceBadge,
  ExplainToggle,
  InferenceMark,
  SampleCount,
  ScoreBreakdown,
} from '../../components/inference/Inference';
import { formatCompact, formatDateTime, formatRatioAsPercent } from '../../lib/format';
import { useT } from '../../i18n';

export function PlanResult({
  plan,
  memberById,
}: {
  plan: BearPlanResponse;
  memberById: Map<string, string>;
}) {
  const t = useT();

  const name = (playerId: string, fallback: string | null) =>
    fallback ?? memberById.get(playerId) ?? playerId;

  return (
    <div className="stack">
      <Card
        title={
          <span className="row">
            {t.planResult.wavePlan} <InferenceMark modelVersion={plan.modelVersion} />
            {plan.ruleVersion ? (
              <span className="faint small">{t.planResult.rules(plan.ruleVersion)}</span>
            ) : null}
          </span>
        }
        actions={
          <ConfidenceBadge
            level={plan.confidence >= 0.7 ? 'HIGH' : plan.confidence >= 0.4 ? 'MEDIUM' : 'LOW'}
            confidence={plan.confidence}
          />
        }
      >
        {plan.waves.length === 0 ? (
          <p className="absent small" style={{ margin: 0 }}>
            {t.planResult.noWaves}
          </p>
        ) : (
          <div className="grid grid--halves">
            {plan.waves.map((wave) => (
              <div
                key={wave.wave}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '10px 12px',
                }}
              >
                <h3 style={{ marginBottom: 8 }}>{t.planResult.waveHeading(wave.wave)}</h3>
                {wave.rallies.length === 0 ? (
                  <p className="absent small" style={{ margin: 0 }}>
                    {t.planResult.noRalliesInWave}
                  </p>
                ) : (
                  <div className="stack" style={{ gap: 10 }}>
                    {wave.rallies.map((rally) => (
                      <div key={`${wave.wave}-${rally.leaderPlayerId}`}>
                        <div className="row">
                          <Link to={`/players/${encodeURIComponent(rally.leaderPlayerId)}`}>
                            <strong>{name(rally.leaderPlayerId, rally.leaderPlayerName)}</strong>
                          </Link>
                          {rally.expectedOpenAt ? (
                            <span className="faint small">
                              {t.planResult.opensAt(formatDateTime(rally.expectedOpenAt) ?? '')}
                            </span>
                          ) : null}
                          {rally.expectedRelativeStrength !== null ? (
                            <Badge tone="neutral" title={t.planResult.relativeStrengthTitle}>
                              {t.planResult.relativeStrength(
                                rally.expectedRelativeStrength.toFixed(2),
                              )}
                            </Badge>
                          ) : null}
                        </div>

                        {rally.preferredJoiners.length > 0 ? (
                          <div className="small muted" style={{ marginTop: 2 }}>
                            {t.planResult.joiners}{' '}
                            {rally.preferredJoiners.map((joiner, index) => (
                              <span key={joiner.playerId} title={joiner.reason}>
                                {index > 0 ? ', ' : ''}
                                {name(joiner.playerId, joiner.playerName)}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <div className="absent small" style={{ marginTop: 2 }}>
                            {t.planResult.noJoinerPreference}
                          </div>
                        )}

                        {rally.backupLead ? (
                          <div className="small muted">
                            {t.planResult.backupLead(name(rally.backupLead, null))}
                          </div>
                        ) : (
                          <div className="absent small">{t.planResult.noBackupLead}</div>
                        )}

                        {rally.explanation.length > 0 ? (
                          <ExplainToggle label={t.planResult.whyThisLead}>
                            <ul className="small muted" style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                              {rally.explanation.map((line) => (
                                <li key={line}>{line}</li>
                              ))}
                            </ul>
                          </ExplainToggle>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title={t.planResult.assumptionsTitle}>
        {plan.assumptions.length === 0 ? (
          <p className="absent small" style={{ margin: 0 }}>
            {t.planResult.noAssumptions}
          </p>
        ) : (
          <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {plan.assumptions.map((assumption) => (
              <li key={assumption}>{assumption}</li>
            ))}
          </ul>
        )}
      </Card>

      <WarningList warnings={plan.warnings} />

      <Card title={t.planResult.leadRankingTitle}>
        <p className="muted small" style={{ marginTop: 0 }}>
          {t.planResult.leadRankingBlurb}
        </p>

        {plan.leadRanking.length === 0 ? (
          <p className="absent small" style={{ margin: 0 }}>
            {t.planResult.noCandidates}
          </p>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>{t.planResult.columns.rank}</th>
                  <th>{t.common.player}</th>
                  <th className="num">{t.planResult.columns.leadScore}</th>
                  <th>{t.common.confidence}</th>
                  <th className="num">{t.planResult.columns.observedAvgDamage}</th>
                  <th>{t.common.samples}</th>
                  <th className="num">{t.planResult.columns.wave}</th>
                  <th>{t.planResult.columns.explain}</th>
                </tr>
              </thead>
              <tbody>
                {plan.leadRanking.map((candidate, index) => (
                  <tr key={candidate.playerId}>
                    <td className="num faint">{index + 1}</td>
                    <td>
                      <Link to={`/players/${encodeURIComponent(candidate.playerId)}`}>
                        <Value absent={t.common.unnamedPlayer}>
                          {candidate.playerName ?? memberById.get(candidate.playerId) ?? null}
                        </Value>
                      </Link>
                    </td>
                    <td className="num">{candidate.leadScore.toFixed(0)}</td>
                    <td>
                      <ConfidenceBadge
                        level={candidate.confidenceLevel}
                        confidence={candidate.confidence}
                      />
                    </td>
                    <td className="num">
                      {candidate.observedAvgDamage === null ? (
                        <span className="absent">{t.planResult.neverLed}</span>
                      ) : (
                        formatCompact(candidate.observedAvgDamage)
                      )}
                    </td>
                    <td>
                      <SampleCount samples={candidate.samples} noun={t.inference.nouns.leads} />
                    </td>
                    <td className="num">
                      {candidate.recommendedWave === null ? (
                        <span className="absent">{t.common.dash}</span>
                      ) : (
                        candidate.recommendedWave
                      )}
                    </td>
                    <td>
                      <ExplainToggle label={t.planResult.breakdown}>
                        <ScoreBreakdown
                          components={candidate.components}
                          warnings={candidate.warnings}
                        />
                        {candidate.reasons.length > 0 ? (
                          <ul className="small muted" style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                            {candidate.reasons.map((reason) => (
                              <li key={reason}>{reason}</li>
                            ))}
                          </ul>
                        ) : null}
                      </ExplainToggle>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="grid grid--stats">
        <Stat
          label={t.planResult.planConfidence}
          value={formatRatioAsPercent(plan.confidence)}
          foot={<span className="faint">{t.planResult.planConfidenceFoot}</span>}
        />
        <Stat
          label={t.planResult.storedAs}
          value={plan.recommendationRunId ? t.planResult.recorded : null}
          foot={
            plan.recommendationRunId ? (
              <span className="mono faint">{plan.recommendationRunId}</span>
            ) : (
              <span className="faint">{t.planResult.notPersistedFoot}</span>
            )
          }
          absentLabel={t.common.notRecordedTitle}
        />
      </div>
    </div>
  );
}
